import React, { useCallback, useEffect, useState } from "react";
import { ShieldOff, ShieldCheck, Loader } from "lucide-react";
import Card from "./Card";
import AiResponseActionsPanel from "./AiResponseActionsPanel";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { asSentence, isWithheld } from "../utils/aiFleetPortfolio";
import { cardTone, hasOpenAction, techsFor } from "../utils/aiRespondRows";

/**
 * The containment surface (§8.8): every asset a response action can currently reach, one row
 * each, with the request buttons a single click from the preflight. Speed comes from fewer
 * clicks to reach the decision, never from skipping it — each row embeds the same
 * AiResponseActionsPanel the asset detail pane uses, so the safety contract (impact statement,
 * named approver or reason-and-confirm immediate path, separation of duties) is identical.
 */

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (secs < 3600) return `${Math.max(1, Math.floor(secs / 60))}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

// Colors match the inventory's source badges so the same provider reads the same everywhere.
// The classification logic itself lives in utils/aiRespondRows (tested there).
const TECH_BADGE = {
  Entra: "bg-blue-50 text-blue-700 border border-blue-200",
  AWS: "bg-amber-50 text-amber-700 border border-amber-200",
  OpenAI: "bg-teal-50 text-teal-700 border border-teal-200",
  Anthropic: "bg-rose-50 text-rose-700 border border-rose-200",
};

// The card's left edge answers "what does this row want from me" before anyone reads it.
const TONE_CLASS = {
  open: "border-l-4 border-l-amber-400",
  applied: "border-l-4 border-l-sky-400",
  actionable: "border-l-4 border-l-red-300",
};

export default function AiRespondTab({ onOpenAsset, refreshKey, overrideAllowed, tier1ImmediateAllowed, irreversibleAllowed }) {
  const apiCall = useApi();
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Why the lists could not be read (a failed request or a withheld envelope), or null when both
  // answered. An unanswered list must never render as "no assets can be contained".
  const [unavailable, setUnavailable] = useState(null);
  const { canViewAllAiEstateData } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canViewEstate = canViewAllAiEstateData(org?.organizationId);

  const load = useCallback(async () => {
    // Both lists are org-wide: without the capability there is nothing to ask for.
    if (!canViewEstate) {
      setUnavailable("Organization-wide response actions need organization-level access.");
      setLoading(false);
      return;
    }
    const [actionable, allActions] = await Promise.all([
      apiCall("GET", "/ai-assets/response-actions/actionable").catch(() => null),
      apiCall("GET", "/ai-assets/response-actions").catch(() => null),
    ]);
    const withheld = [actionable, allActions].find(isWithheld);
    if (!Array.isArray(actionable) || !Array.isArray(allActions)) {
      setRows([]);
      setActions([]);
      setUnavailable(withheld ? asSentence(withheld.reason) : "The response actions could not be read.");
    } else {
      setRows(actionable);
      setActions(allActions);
      setUnavailable(null);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewEstate]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-sm text-content-secondary">
          <Loader className="w-4 h-4 animate-spin" /> Reading which assets can be contained…
        </div>
      </Card>
    );
  }

  if (unavailable) {
    return (
      <div data-testid="respond-unavailable">
        <Card className="p-6">
          <div className="flex items-start gap-2 text-sm text-content-secondary">
            <ShieldOff className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-content-primary">Response actions are unavailable</div>
              <p>{unavailable}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const actionsByAsset = new Map();
  for (const a of actions) {
    if (a.aiAssetId == null) continue;
    if (!actionsByAsset.has(a.aiAssetId)) actionsByAsset.set(a.aiAssetId, []);
    actionsByAsset.get(a.aiAssetId).push(a);
  }

  // Rows with an open decision surface first; then by name so the list is stable.
  const sorted = [...rows].sort((a, b) => {
    const openA = hasOpenAction(actionsByAsset.get(a.id));
    const openB = hasOpenAction(actionsByAsset.get(b.id));
    if (openA !== openB) return openA ? -1 : 1;
    return (a.name || "").localeCompare(b.name || "");
  });

  // Actions on assets that are no longer actionable (connector disabled, response turned off,
  // asset removed). The audit trail must not disappear with the capability — and open decisions
  // on them still need deciding.
  const rowIds = new Set(rows.map((r) => r.id));
  const historyOnly = new Map();
  for (const [assetId, list] of actionsByAsset) {
    if (!rowIds.has(assetId)) historyOnly.set(assetId, list);
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
        <p className="text-xs text-content-secondary">
          Every asset a response action can currently reach. Requesting here follows the same
          contract as everywhere else: a live impact statement first, then a named approver — or an
          immediate execution that records who, why, and notifies the other risk managers.
        </p>
        <span className="flex items-center gap-1.5 text-[11px] text-content-secondary">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-300" /> action available
        </span>
        <span className="flex items-center gap-1.5 text-[11px] text-content-secondary">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-400" /> decision in flight
        </span>
        <span className="flex items-center gap-1.5 text-[11px] text-content-secondary">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-400" /> containment applied
        </span>
      </div>

      {sorted.length === 0 && historyOnly.size === 0 && (
        <Card className="p-6">
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-content-secondary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-content-primary">No assets can be contained right now.</p>
              <p className="text-xs text-content-secondary mt-1">
                An asset appears here when Enable response actions is on in AI Intelligence
                settings and discovery holds the identity evidence an action needs — an Entra
                service principal, an AWS agent role, a GCP service account, an OpenAI project,
                or an Anthropic workspace. That setting governs every action; disabling an Entra
                identity also needs response consent on the Microsoft connection. This is a statement about
                reach, not about risk: assets Axaris cannot act on may still carry findings in
                the inventory.
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {sorted.map((row) => (
          <Card key={row.id} className={`p-4 ${TONE_CLASS[cardTone(row, actionsByAsset.get(row.id))]}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <ShieldOff className="w-4 h-4 text-content-secondary shrink-0" />
              <button
                onClick={() => onOpenAsset?.(row.id)}
                className="text-sm font-semibold text-content-primary hover:underline"
                title="Open in the inventory"
              >
                {row.name}
              </button>
              <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-300">
                {row.kind}
              </span>
              {techsFor((row.available || []).filter((a) => a?.isAvailable === true).map((a) => a.actionType)).map((tech) => (
                <span
                  key={tech}
                  className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${TECH_BADGE[tech]}`}
                >
                  {tech}
                </span>
              ))}
              {row.tier != null && (
                <span
                  title={row.tier === 1
                    ? "Tier 1 — response actions always require a second person's approval"
                    : `Tier ${row.tier}`}
                  className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                    row.tier === 1
                      ? "bg-purple-100 text-purple-800 border border-purple-300"
                      : "bg-gray-100 text-gray-600 border border-gray-300"
                  }`}
                >
                  Tier {row.tier}
                </span>
              )}
              {row.containmentState === "Contained" && (
                <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-300">
                  Containment applied
                </span>
              )}
              {row.status === "Stale" && (
                <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  Stale
                </span>
              )}
              <span className="ml-auto text-[11px] text-content-secondary">
                {row.ownerUpn || <span className="text-orange-600">No owner</span>}
                {row.lastSeenAt && <> · seen {timeAgo(row.lastSeenAt)}</>}
              </span>
            </div>
            <AiResponseActionsPanel
              assetId={row.id}
              assetName={row.name}
              available={row.available || []}
              actions={actionsByAsset.get(row.id) || []}
              onChanged={load}
              // Tier 1 refuses the immediate path server-side unless the org's §11i policy
              // allows it; the form mirrors the server either way.
              overrideAllowed={overrideAllowed && (row.tier !== 1 || tier1ImmediateAllowed)}
              irreversibleAllowed={irreversibleAllowed}
              showHeader={false}
            />
          </Card>
        ))}
      </div>

      {historyOnly.size > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-content-secondary uppercase mb-2">
            Actions on assets no longer reachable
          </div>
          <div className="space-y-3">
            {[...historyOnly.entries()].map(([assetId, list]) => (
              <Card key={assetId} className="p-4 border-l-4 border-l-gray-300">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-content-primary">
                    {list[0]?.targetName || `Asset ${assetId}`}
                  </span>
                  {techsFor(list.map((a) => a.actionType)).map((tech) => (
                    <span
                      key={tech}
                      className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${TECH_BADGE[tech]}`}
                    >
                      {tech}
                    </span>
                  ))}
                  <span className="text-[11px] text-content-secondary">
                    no action is currently available — response actions turned off, connector disabled or evidence gone
                  </span>
                </div>
                <AiResponseActionsPanel
                  assetId={assetId}
                  available={[]}
                  actions={list}
                  onChanged={load}
                  overrideAllowed={overrideAllowed}
                  irreversibleAllowed={irreversibleAllowed}
                  showHeader={false}
                />
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
